import { RiMoneyEuroBoxLine } from 'react-icons/ri';

import {
	ROUTE_AVAILABILITY,
	ROUTE_CALENDAR_LIST,
	ROUTE_DASHBOARD_BOOKINGS,
	ROUTE_DASHBOARD_BOOKING_PAGES,
	ROUTE_DASHBOARD_PARTNERS,
	ROUTE_DASHBOARD_PRICING_POLICIES_LIST,
	ROUTE_ROOT_PAGES_LIST,
} from '@/constants/routes';
import { LSK_SIDEBAR_VISIBILITY } from '@/constants/storage-keys';
import useAuth from '@/hooks/useAuth';
import useFeature from '@/hooks/useFeature';
import { useDisclosure } from '@chakra-ui/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiBookOpen, FiCalendar, FiClock, FiGrid, FiShare2, FiTool } from 'react-icons/fi';

export function useAppShellController() {
	const auth = useAuth();
	const { t } = useTranslation();
	const { isOpen, onClose, onToggle } = useDisclosure({
		defaultIsOpen: computeIfMenuIsVisibleByDefault(),
	});
	const canSeeCalendars = useFeature('CALENDARS');
	const canSeePricingPolicies = useFeature('PRICING_POLICY');

	const ref = useRef<HTMLDivElement>(null);

	function handleClose() {
		localStorage.setItem(LSK_SIDEBAR_VISIBILITY, 'hidden');
		onClose();
	}

	function handleToggle() {
		isOpen
			? localStorage.setItem(LSK_SIDEBAR_VISIBILITY, 'hidden')
			: localStorage.setItem(LSK_SIDEBAR_VISIBILITY, 'visible');
		onToggle();
	}

	const links = [
		{ text: t('Reservation calendars'), icon: FiGrid, to: ROUTE_DASHBOARD_BOOKING_PAGES },
		{ text: t('Bookings'), icon: FiBookOpen, to: ROUTE_DASHBOARD_BOOKINGS },
		{ text: t('Link groups'), icon: FiShare2, to: ROUTE_ROOT_PAGES_LIST },
		{ text: t('Availability'), icon: FiClock, to: ROUTE_AVAILABILITY },
	];

	if (canSeeCalendars) {
		links.splice(2, 0, { text: t('Calendars'), icon: FiCalendar, to: ROUTE_CALENDAR_LIST });
	}

	if (auth.getCurrentUser().tenant.role === 'ADMIN') {
		links.push({ text: t('Admin'), icon: FiTool, to: ROUTE_DASHBOARD_PARTNERS });
	}

	if (canSeePricingPolicies) {
		links.push({ text: t('Pricing policies'), icon: RiMoneyEuroBoxLine, to: ROUTE_DASHBOARD_PRICING_POLICIES_LIST });
	}

	return {
		ref,
		t,
		links,
		isOpen,
		handleToggle,
		handleClose,
	};
}

function computeIfMenuIsVisibleByDefault(): boolean {
	// On mobile is always closed by default
	if (window.innerWidth <= 992) {
		return false;
	}
	// On desktop depends on user configuration
	if (localStorage.getItem(LSK_SIDEBAR_VISIBILITY) === 'visible') {
		return true;
	}

	if (localStorage.getItem(LSK_SIDEBAR_VISIBILITY) === 'hidden') {
		return false;
	}

	// By default is hidden
	return false;
}
